import { FEATURE_PLAN_MAP, type FeatureKey, type PlanId } from '@/config/access-control'
import { PLAN_HIERARCHY } from '@/config/plans'
import { useCurrentPlan } from './useCurrentPlan'

export type FeatureAccessResult =
	| { allowed: true; requiredPlan: PlanId | null; currentPlan: PlanId | null }
	| { allowed: false; reason: 'plan-locked'; requiredPlan: PlanId; currentPlan: PlanId }

/**
 * Returns plan-gate state for the given feature.
 *
 * Fail-open when currentPlan is null (e.g. platform deployments without Chargebee):
 * plan-gating is a billing concept that doesn't apply there.
 */
export function useFeatureAccess(featureKey?: FeatureKey): FeatureAccessResult {
	const currentPlan = useCurrentPlan()

	if (!featureKey) {
		return { allowed: true, requiredPlan: null, currentPlan }
	}

	const required = FEATURE_PLAN_MAP[featureKey]

	// No plan concept on this deployment — fail open.
	if (currentPlan == null) {
		return { allowed: true, requiredPlan: required, currentPlan }
	}

	const currentIdx = PLAN_HIERARCHY.indexOf(currentPlan as (typeof PLAN_HIERARCHY)[number])
	const requiredIdx = PLAN_HIERARCHY.indexOf(required as (typeof PLAN_HIERARCHY)[number])

	// Unknown plan — fail open; the UI shouldn't block users based on plans we can't interpret.
	if (currentIdx === -1 || requiredIdx === -1) {
		return { allowed: true, requiredPlan: required, currentPlan }
	}

	return currentIdx >= requiredIdx
		? { allowed: true, requiredPlan: required, currentPlan }
		: { allowed: false, reason: 'plan-locked', requiredPlan: required, currentPlan }
}
